import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Database, AlertCircle, ExternalLink } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import {
  getCustomFieldDefinitions,
  getCustomFieldValues,
  createCustomFieldValue,
  deleteCustomFieldValue,
  getAvailableWorkspaces
} from '../../services/customFieldsApi';
import {
  CustomFieldDefinition,
  CustomFieldValue,
  Workspace
} from '../../types/custom-fields';
import CustomFieldInput from './CustomFieldInput';
import api from '../../lib/api';
import Button from '@shared/ui/Button';

interface CustomFieldEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityId: string;
  entityDisplayName: string;
  workspaceId?: string;
  cachedOrganizationId?: string | null;
  onLoadingChange?: (loading: boolean) => void;
  onSave?: () => void; // Callback after successful save
}

const CustomFieldEditModal: React.FC<CustomFieldEditModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityDisplayName,
  workspaceId,
  cachedOrganizationId,
  onLoadingChange,
  onSave
}) => {
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  // Track original values with their IDs for deletion when cleared
  const [originalValues, setOriginalValues] = useState<Record<string, { valueId: string; value: unknown }>>({});
  const [availableWorkspaces, setAvailableWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (isOpen) {
      // Start loading immediately when modal opens
      setLoading(true);
      onLoadingChange?.(true);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityType, entityId, workspaceId]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      onLoadingChange?.(true);
      setError(null);
      
      // Get organization ID - use cached if available, otherwise fetch from workspace
      let organizationId: string | null = cachedOrganizationId || null;
      
      if (!organizationId && workspaceId) {
        try {
          const response = await api.get(`/workspaces/${workspaceId}`);
          organizationId = response.data.data?.owner_org_id || response.data.owner_org_id;
        } catch (err: any) {
          // Silently fail and continue to error handling below
        }
      }
      
      if (!organizationId) {
        setError('Unable to determine organization. Please try again.');
        setLoading(false);
        onLoadingChange?.(false);
        return;
      }
      
      // Fetch field definitions, existing values, and available workspaces in parallel
      const [definitions, valuesData, workspaces] = await Promise.all([
        getCustomFieldDefinitions(organizationId, entityType, true),
        getCustomFieldValues(entityType, entityId, workspaceId).catch(() => ({ values: [], workspaces: [] })),
        getAvailableWorkspaces(entityType, entityId).catch(() => [])
      ]);
      
      setCustomFieldDefinitions(definitions);
      setAvailableWorkspaces(workspaces);
      
      // Pre-populate form with existing values and track originals for deletion
      const valueMap: Record<string, unknown> = {};
      const originals: Record<string, { valueId: string; value: unknown }> = {};
      valuesData.values.forEach((v: CustomFieldValue) => {
        const actualValue = v.value?.value !== undefined ? v.value.value : v.value;
        valueMap[v.field_definition_id] = actualValue;
        // Track original value with its ID so we can delete it if cleared
        originals[v.field_definition_id] = {
          valueId: v.id,
          value: actualValue
        };
      });
      setFormValues(valueMap);
      setOriginalValues(originals);
      
      // Pre-select current workspace if provided
      if (workspaceId) {
        setSelectedWorkspaceIds([workspaceId]);
      } else if (workspaces.length > 0) {
        // Pre-select all workspaces if viewing across workspaces
        setSelectedWorkspaceIds(workspaces.map(w => w.id));
      }
    } catch (err) {
      setError('Failed to load custom fields. Please try again.');
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  };
  
  const handleSave = async () => {
    if (selectedWorkspaceIds.length === 0) {
      setError('Please select at least one workspace');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Helper to check if value is empty
      const isEmpty = (val: unknown): boolean => {
        return val === undefined || val === null || val === '';
      };
      
      // Save each custom field value that has been set (non-empty values)
      const savePromises = customFieldDefinitions
        .filter(def => !isEmpty(formValues[def.id]))
        .map(def => 
          createCustomFieldValue({
            field_definition_id: def.id,
            entity_type: entityType,
            entity_id: entityId,
            entity_display_name: entityDisplayName,
            workspace_ids: selectedWorkspaceIds,
            value: formValues[def.id]
          })
        );
      
      // Delete values that were cleared (had original value but now empty)
      const deletePromises = customFieldDefinitions
        .filter(def => {
          const hadValue = originalValues[def.id] !== undefined;
          const nowEmpty = isEmpty(formValues[def.id]);
          return hadValue && nowEmpty;
        })
        .map(def => deleteCustomFieldValue(originalValues[def.id].valueId));
      
      await Promise.all([...savePromises, ...deletePromises]);
      
      // Call onSave callback to refresh count badge
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (err) {
      setError('Failed to save custom fields. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaceIds(prev => 
      prev.includes(workspaceId)
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };
  
  return (
    <Modal
      isOpen={isOpen}
      title="Edit custom fields"
      description={entityDisplayName}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={
              saving || selectedWorkspaceIds.length === 0 || customFieldDefinitions.length === 0
            }
          >
            Save
          </Button>
        </>
      }
    >
          {error && (
            <div className="mb-4 p-4 border border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-900/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error-800 dark:text-error-300">{error}</p>
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {/* Workspace Selection */}
              {availableWorkspaces.length > 1 && (
                <div className="surface-inset mb-6">
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-3">
                    Apply to Workspaces
                  </label>
                  <div className="space-y-2">
                    {availableWorkspaces.map(workspace => (
                      <label key={workspace.id} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedWorkspaceIds.includes(workspace.id)}
                          onChange={() => handleWorkspaceToggle(workspace.id)}
                          className="h-4 w-4 text-primary-700 dark:text-primary-600 border-border-light dark:border-border-dark rounded focus:ring-primary-500 dark:focus:ring-primary-400"
                        />
                        <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">
                          {workspace.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedWorkspaceIds.length === 0 && (
                    <p className="mt-2 text-xs text-error-600 dark:text-error-400">
                      Please select at least one workspace
                    </p>
                  )}
                </div>
              )}
              
              {/* Custom Fields Form */}
              {customFieldDefinitions.length === 0 ? (
                <div className="text-center py-8">
                  <Database className="h-12 w-12 mx-auto text-text-tertiary dark:text-text-dark-tertiary mb-3" />
                  <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
                    No custom fields have been defined yet
                  </p>
                  <Link
                    to="/organization-settings?tab=custom-fields"
                    onClick={onClose}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-700 dark:bg-primary-600 text-white rounded hover:bg-primary-800 dark:hover:bg-primary-700 transition-colors"
                  >
                    <Database className="h-4 w-4" />
                    Create Custom Fields
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {customFieldDefinitions.map(field => (
                    <div key={field.id}>
                      <CustomFieldInput
                        field={field}
                        value={formValues[field.id]}
                        onChange={(value: unknown) => setFormValues({ ...formValues, [field.id]: value })}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

    </Modal>
  );
};

export default CustomFieldEditModal;
